import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getDataAPI, postDataAPI } from "../../utils/fetchData";
import LayoutHeader from "../../layout/LayoutHeader";
import Modal from "../../components/Modal";
import { useModal } from "../../hooks/useModal";

const EditMerch = () => {
    const { merchId } = useParams();
    const [editData, setEditData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const token = localStorage.getItem('dizeeToken');
    const navigate = useNavigate();
    const { modalConfig, showModal, showConfirm, closeModal } = useModal();

    useEffect(() => {
        loadMerchData();
    }, [merchId]);

    const loadMerchData = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            const response = await getDataAPI('user/getMerch', token);
            
            if (response.data.status === 200 && response.data.data) {
                const merchData = Array.isArray(response.data.data) ? response.data.data : response.data.data.data;
                const merchItem = merchData.find(item => item._id === merchId);
                
                if (merchItem) {
                    setEditData(merchItem);
                } else {
                    showModal('Not Found', 'Merch item not found', 'error');
                    navigate('/merch/view-all');
                }
            }
        } catch (error) {
            console.error('Error loading merch item:', error);
            showModal('Load Error', 'Error loading merch data', 'error');
        } finally {
            setIsLoadingData(false);
        }
    };

    const saveData = async () => {
        setIsLoading(true);
        try {
            let response = await postDataAPI('user/updateMerch', {
                id: merchId,
                data: editData
            }, token);
            console.log(`response::`, response.data);
            setTimeout(() => {
                navigate('/merch/view-all');
            }, 1500);
        } catch (error) {
            console.error('Error updating merch item:', error);
            showModal('Update Error', 'Error updating merch item. Please try again.', 'error');
        }
        setIsLoading(false);
    }

    const deleteMerchItem = async () => {
        setIsDeleting(true);
        try {
            let response = await postDataAPI('user/deleteMerch', {
                id: merchId
            }, token);
            console.log(`delete response::`, response.data);
            showModal('Success', 'Merch item deleted successfully!', 'success');
            setTimeout(() => {
                navigate('/merch/view-all');
            }, 1500);
        } catch (error) {
            console.error('Error deleting merch item:', error);
            showModal('Delete Error', 'Error deleting merch item. Please try again.', 'error');
        }
        setIsDeleting(false);
        setShowDeleteConfirm(false);
    }

    const basicFields = [
        { key: 'product_name', label: 'Product Name', placeholder: 'Enter product name' },
        { key: 'vendor', label: 'Vendor/Brand', placeholder: 'Enter vendor or brand name' },
        { key: 'product_type', label: 'Product Type', placeholder: 'Enter product type (e.g., T-Shirt, Hoodie)' },
        { key: 'price', label: 'Price', placeholder: 'Enter price', type: 'number' },
        { key: 'compare_at_price', label: 'Compare At Price', placeholder: 'Enter compare price', type: 'number' },
        { key: 'sku', label: 'SKU', placeholder: 'Enter SKU code' },
        { key: 'barcode', label: 'Barcode', placeholder: 'Enter barcode' },
        { key: 'inventory_quantity', label: 'Inventory Quantity', placeholder: 'Enter quantity', type: 'number' },
        { key: 'weight', label: 'Weight', placeholder: 'Enter weight' },
        { key: 'status', label: 'Status', placeholder: 'Select status', type: 'select', options: ['active', 'draft', 'archived'] },
        { key: 'description', label: 'Description', placeholder: 'Enter product description', type: 'textarea' }
    ];

    const advancedFields = [
        { key: 'variant_title', label: 'Variant Title', placeholder: 'Enter variant title' },
        { key: 'option1', label: 'Option 1 (Size)', placeholder: 'Enter size option' },
        { key: 'option2', label: 'Option 2 (Color)', placeholder: 'Enter color option' },
        { key: 'option3', label: 'Option 3', placeholder: 'Enter additional option' },
        { key: 'inventory_policy', label: 'Inventory Policy', placeholder: 'Enter inventory policy' },
        { key: 'fulfillment_service', label: 'Fulfillment Service', placeholder: 'Enter fulfillment service' },
        { key: 'inventory_management', label: 'Inventory Management', placeholder: 'Enter inventory management' },
        { key: 'requires_shipping', label: 'Requires Shipping', type: 'checkbox' },
        { key: 'taxable', label: 'Taxable', type: 'checkbox' },
        { key: 'tax_code', label: 'Tax Code', placeholder: 'Enter tax code' },
        { key: 'cost_per_item', label: 'Cost Per Item', placeholder: 'Enter cost per item', type: 'number' },
        { key: 'profit_margin', label: 'Profit Margin', placeholder: 'Enter profit margin' },
        { key: 'supplier', label: 'Supplier', placeholder: 'Enter supplier information' },
        { key: 'material', label: 'Material', placeholder: 'Enter material information' },
        { key: 'care_instructions', label: 'Care Instructions', placeholder: 'Enter care instructions', type: 'textarea' },
        { key: 'tags', label: 'Tags', placeholder: 'Enter tags (comma separated)', type: 'textarea' },
        { key: 'seo_title', label: 'SEO Title', placeholder: 'Enter SEO title' },
        { key: 'seo_description', label: 'SEO Description', placeholder: 'Enter SEO description', type: 'textarea' }
    ];

    const renderField = (field) => {
        const fieldClass = "bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#9932CC] hover:bg-[rgba(153,50,204,0.05)] transition-all";
        
        return (
            <div key={field.key} className={fieldClass}>
                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                    {field.label}
                </label>
                {field.type === 'textarea' ? (
                    <textarea
                        value={editData[field.key] || ''}
                        onChange={(e) => setEditData({
                            ...editData,
                            [field.key]: e.target.value
                        })}
                        placeholder={field.placeholder}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] resize-none h-20"
                    />
                ) : field.type === 'select' ? (
                    <select
                        value={editData[field.key] || ''}
                        onChange={(e) => setEditData({
                            ...editData,
                            [field.key]: e.target.value
                        })}
                        className="w-full bg-[#1A1A1A] border-none outline-none text-white text-[12px] rounded-md p-2"
                    >
                        <option value="">{field.placeholder}</option>
                        {field.options?.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                ) : field.type === 'checkbox' ? (
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            checked={editData[field.key] || false}
                            onChange={(e) => setEditData({
                                ...editData,
                                [field.key]: e.target.checked
                            })}
                            className="mr-2 w-4 h-4 text-[#9932CC] bg-transparent border border-[rgba(255,255,255,0.3)] rounded focus:ring-[#9932CC] focus:ring-2"
                        />
                        <span className="text-[12px] text-[rgba(255,255,255,0.7)]">Enable this option</span>
                    </div>
                ) : (
                    <input
                        type={field.type || 'text'}
                        value={editData[field.key] || ''}
                        onChange={(e) => setEditData({
                            ...editData,
                            [field.key]: e.target.value
                        })}
                        placeholder={field.placeholder}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                    />
                )}
            </div>
        );
    };

    if (isLoadingData) {
        return (
            <LayoutHeader>
                <div className="flex justify-center w-full">
                    <div className="w-[390px] h-[80vh] flex flex-col items-center">
                        <div className="text-white text-left py-[50px]">Loading merch data...</div>
                    </div>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Edit Merch Item</span>
                        <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => navigate(-1)}>Go back</span>
                    </div>

                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        <div className="flex flex-col">
                            <div className="mb-6">
                                <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Edit merch details</span>
                                <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">Update the information for this merch item</p>
                            </div>

                            {/* Basic Fields */}
                            <div className="space-y-4 mb-6">
                                {basicFields.map(field => renderField(field))}
                            </div>

                            {/* Advanced Section Toggle */}
                            <div className="mb-4">
                                <button
                                    onClick={() => setShowAdvanced(!showAdvanced)}
                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#9932CC] hover:bg-[rgba(153,50,204,0.05)] transition-all flex items-center justify-between"
                                >
                                    <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Advanced Fields</span>
                                    <svg 
                                        className={`w-4 h-4 text-white transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                            </div>

                            {/* Advanced Fields */}
                            {showAdvanced && (
                                <div className="space-y-4 mb-6">
                                    <div className="text-[10px] text-[rgba(255,255,255,0.7)] mb-4">
                                        Advanced merch details for comprehensive tracking and AI integration
                                    </div>
                                    {advancedFields.map(field => renderField(field))}
                                </div>
                            )}

                            {/* Save Button */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#9932CC] hover:bg-[rgba(153,50,204,0.05)] transition-all">
                                <button
                                    onClick={saveData}
                                    disabled={isLoading}
                                    className="w-full bg-[#9932CC] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(153,50,204,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                            Updating...
                                        </>
                                    ) : (
                                        'Update Merch Item'
                                    )}
                                </button>
                            </div>

                            {/* Delete Button */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF2300] hover:bg-[rgba(255,35,0,0.05)] transition-all mt-4">
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={isLoading || isDeleting}
                                    className="w-full bg-[#FF2300] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(255,35,0,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    Delete Merch Item
                                </button>
                            </div>
                        </div>

                        {/* Help Text */}
                        <div className="mt-6 mb-4">
                            <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                                <p>Update any fields as needed.</p>
                                <p>Advanced fields help provide comprehensive data for AI assistance.</p>
                                <p>Changes will be saved and available to your AI assistant.</p>
                            </div>
                        </div>
                            </div>

                    {/* Delete Confirmation Modal */}
                            {showDeleteConfirm && (
                                <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
                                    <div className="w-full max-w-[350px] mx-4 transform overflow-hidden rounded-2xl bg-black border-2 border-[#FF2300] p-6 text-left align-middle shadow-xl transition-all">
                                        {/* Header */}
                                        <div className="flex items-center justify-between mb-6 relative">
                                            <h3 className="text-white text-[18px] font-bold text-left">Delete Merch Item</h3>
                                            <button
                                                onClick={() => setShowDeleteConfirm(false)}
                                                className="text-gray-400 hover:text-white transition-colors"
                                                disabled={isDeleting}
                                            >
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>

                                        {/* Content */}
                                        <div className="mb-8">
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] text-left">
                                                Are you sure you want to delete this merch item? This action cannot be undone.
                                            </p>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex space-x-4">
                                            <button
                                                onClick={deleteMerchItem}
                                                disabled={isDeleting}
                                                className="flex-1 bg-[#FF2300] text-white text-[12px] font-semibold py-3 px-4 rounded-lg hover:bg-[#FF2300]/90 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                            >
                                                {isDeleting ? (
                                                    <>
                                                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                                                        Deleting...
                                                    </>
                                                ) : (
                                                    'Delete'
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setShowDeleteConfirm(false)}
                                                disabled={isDeleting}
                                                className="flex-1 bg-transparent border-2 border-[#FF2300] text-white text-[12px] font-semibold py-3 px-4 rounded-lg hover:bg-[#FF2300]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                </div>
            </div>
            
            <Modal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                showCancel={modalConfig.showCancel}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
            />
        </LayoutHeader>
    );
};

export default EditMerch; 